import React, { useState, useCallback, useEffect, useMemo } from 'react';
import Header from '../../components/Header';
import styles from '../../styles/Search.module.css';
import Container from 'react-bootstrap/Container'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import { commonUrl } from '../api/config';
import algoliasearch from 'algoliasearch';
import Image from 'next/image';
import Footer from '../../components/Footer';
import parse, { domToReact } from 'html-react-parser';
import * as LIB from '../../lib/common';
import SearchBar from './searchBar';
import Keyword from './keyword';
import ResultCountBar from './resultCountBar';
import Pagination from './pagination';

const getCurrentYear = () => new Date().getFullYear();

const SearchView = () => {
    // Algolia client is created once per page render cycle and reused by search handlers.
    const client = algoliasearch(
        process.env.NEXT_PUBLIC_ALGOLIA_APPLICATION_ID, 
        process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_ONLY_API_KEY
    );
    const [searchTxt, setSearchTxt] = useState('');
    const [searchResult, setResult] = useState([]);
    const [topics, setTopics] = useState([]);
    const [resultCount, setResultCount] = useState(0);
    const [curPageNum, setCurPageNum] = useState(0);
    const [totalPageNum, setTotalPageNum] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [keywords, setKeywords] = useState([]);
    const [filterData, setFilterData] = useState([]);
    const [didSearchTxt, setDidSearchTxt] = useState('');
    const [preciseMode, setPreciseMode] = useState(true);

    const [category, setCategory] = useState([]);
    const [openCategory, setOpenCategory] = useState(false);

    const [dateFilter, setDateFilter] = useState([getCurrentYear(), getCurrentYear()]);
    const [openDateFilter, setOpenDateFilter] = useState(false);
    const [dateRangeUnix, setDateRangeUnix] = useState(null);

    //value: def(=default), n2o(=new to old), o2n(=old to new), 
    const [sorting, setSorting] = useState('def');
    const [openSorting, setOpenSorting] = useState(false);

    useEffect(() => {
        // Load static metadata (topics + preset keywords) when page initializes.
        getMenuData();
        getKeywordData();
    }, [])

    const onSortingComplete = useCallback((sortingType) => {
        // "!preciseMode" will persist the search mode, ref to doSearch() to read more.
        doSearch('', 0, sortingType, !preciseMode, category, dateRangeUnix);
    });

    const onCategoryComplete = useCallback((cat) => {
        // "!preciseMode" will persist the search mode, ref to doSearch() to read more.
        doSearch('', 0, null, !preciseMode, cat, dateRangeUnix);
    });

    const onDateFilterComplete = useCallback((date) => {
        //dateRangeUnix to for presist the date range over diff filter
        setDateRangeUnix(date);
        doSearch('', 0, null, !preciseMode, category, date);
    });

    const getMenuData = async () => {
        const res = await fetch(commonUrl.menu);
        const resData = await res.json();
        let items = [];
        if (resData) {
            // Convert nested menu JSON into a topicId -> topicName lookup table.
            for (const cat in resData) {
                let subcats = resData[cat];
                for (const topicName in subcats) {
                    let topicID = subcats[topicName].id;
                    items[topicID] = topicName;
                }
            }
            setTopics(items);
        }
    }

    const getKeywordData = async () => {
        const res = await fetch(commonUrl.searchKeyword);
        const resData = await res.json();
        if (resData) {
            setKeywords(resData.keywords);
            //  console.log('filter data=', resData.filters);
            setFilterData(resData.filters);
        }
    }

    const doSearch = (txt, targetPage, sortingType, offPreciseMode, cat, dateRange) => {
        // Resolve the effective search text from either input box or clicked keyword.
        if (searchTxt.trim() === '' && txt === '') return;

        let searchWord = (txt && txt !== '') ? txt : searchTxt;

        if (searchWord.trim() === '') return;

        //precise searching mode is on by default
        //user may off it when they press "search more btn"
        if (offPreciseMode !== true) {
            searchWord = `"${searchWord}"`;
        }

        //set target page
        let toPage = targetPage ?? 0;
    
        // Select Algolia index according to selected sorting mode.
        let indexType = process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_INDEX;
        let sortCase = sortingType ?? sorting; 
        switch (sortCase) {
            case 'def':
                indexType = process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_INDEX;
                break;
            case 'n2o':
                indexType = process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_INDEX_BY_DATE_DESC;
                break;
            case 'o2n':
                indexType = process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_INDEX_BY_DATE_ASC;
                break;
            default:
                indexType = process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_INDEX;
        }

        // Build a default safety filter to hide future-dated content.
        let index = client.initIndex(indexType);
        const currentTimestamp = Math.floor(Date.now() / 1000);
        let filterCondition = `post_date_timestamp <= ${currentTimestamp}`;

        ////////////////////
        // Category filter: OR between selected topics, then combined with base filter by AND.
        ////////////////////
        const topicIds = Array.isArray(cat?.type_ids) ? cat.type_ids : [];
        if (topicIds.length > 0) {
            const topicFilter = topicIds
                .map((id) => `topic_id:"${id}"`)
                .join(' OR ');
            filterCondition = `${filterCondition} AND (${topicFilter})`;

            //As for 2012 msg, cat.after will be a date.
            if (cat.after !== '') {
                let startDate = new Date(cat.after);
                let startDateUnix = Math.floor(startDate.getTime() / 1000);
                let endDate = new Date();
                let endDateUnix = Math.floor(endDate.getTime() / 1000);
                let dateRangeFilter = startDateUnix + ' TO ' + endDateUnix;

                filterCondition = `${filterCondition} AND post_date_timestamp:${dateRangeFilter}`;
            }
        }

        ////////////////////
        // Date range filter generated by ResultCountBar date picker.
        ////////////////////
        if (dateRange !== null) {
            filterCondition = `${filterCondition} AND post_date_timestamp:${dateRange}`
        }

        // Execute search and sync all UI states in one place.
        setIsLoading(true);
        setErrorMsg('');
        index.search(searchWord, {
            page: toPage,
            filters: filterCondition,
        })
            .then(({ hits, nbPages, page, nbHits, hitsPerPage }) => {
                // return as a json array of objects
                // console.log('search by=', searchWord, 'page=', toPage, '/result=', hits);
                // console.log('nbpages', nbPages);
                // console.log('current page', page);
                //  console.log('result=', hits);
                // console.log('total page num=', nbHits)
                setResult(hits);
                setResultCount(nbHits);
                setTotalPageNum(nbPages);
                setCurPageNum(page);
                setDidSearchTxt(searchWord);
                window.scrollTo(0, 0);
            })
            .catch(err => {
                console.log(err);
                setErrorMsg('搜尋暫時失敗，請稍後再試。');
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const onchangevalue = (e) => {
        setSearchTxt(e.target.value);
    };

    const viewArticle = (articleID) => {
        if (articleID !== '') {
            window.open('/' + articleID, "_blank");
        }
    };

    const resetSearchFilters = useCallback(() => {
        // Reuse the same reset behavior for keyword click and search button click.
        const currentYear = getCurrentYear();
        setPreciseMode(true);
        setDateRangeUnix(null);
        setCategory([]);
        setDateFilter([currentYear, currentYear]);
    }, []);

    const pressKeyword = (txt) => {
        if (txt !== '') {
            setSearchTxt(txt);
            resetSearchFilters();
            doSearch(txt, 0, null, false, [], null);
        }
    };

    const clickSearchBtn = () => {
        resetSearchFilters();
        doSearch('', 0, null, false, [], null);
    };

    const jumpPage = (page) => {
        //search for next page data
        doSearch('', page, null, !preciseMode, category, dateRangeUnix);
    };

    const navigation = (action) => {
        if (action === 'prev' && curPageNum - 1 >= 0) {
            doSearch('', curPageNum - 1, null, !preciseMode, category, dateRangeUnix);
        }
        else if (action === 'next' && curPageNum + 1 + 1 <= totalPageNum) {
            doSearch('', curPageNum + 1, null, !preciseMode, category, dateRangeUnix);
        }
    };

    const parseOptions = {
        replace: ({ attribs, children }) => {
            if (!attribs) {
                return;
            }

            if (attribs.class === 'search-highlight') {
                return (
                    <span
                        style={{
                            color: '#1f2a37',
                            backgroundColor: 'rgba(255, 191, 0, 0.28)',
                            padding: '1px 5px',
                            borderRadius: '6px',
                            fontWeight: 600,
                            lineHeight: '1.4',
                            boxDecorationBreak: 'clone',
                            WebkitBoxDecorationBreak: 'clone',
                        }}
                    >
                        {domToReact(children, parseOptions)}
                    </span>
                );
            }
        }
    };

    const searchMore = () => {
        //off the precise searching mode
        setPreciseMode(false);
        doSearch('', 0, null, true, category, dateRangeUnix);
    }

    const renderResult = () => {
        let rows = [];

        // If no snippet highlight is available, we show a spaced keyword fallback hint.
        const searchedText = (didSearchTxt || '').replaceAll('"', '');
        let textSplit = searchedText.split('').join(' ');
        let exceptionResultTxt = "包含字眼：" + textSplit;

        if (searchResult.length > 0) {
            //loop through the search result array
            for (let rs = 0; rs < searchResult.length; rs++) {
                const result = searchResult[rs];
                let title = "";
                let description = "";
                let snippet = {};
                let titleMatchLev = "none";
                let fulltextMatchLev = "none";
                let excerptMatchLev = "none";
                
                //prevent undefined value from server accidentally.
                //A = B || C, if B is undefined, then A = C.
                title = result.title || title;
                description = result.excerpt || description;
                snippet = result._snippetResult || snippet;
                titleMatchLev = snippet?.title?.matchLevel || titleMatchLev;
                fulltextMatchLev = snippet?.fulltext?.matchLevel || fulltextMatchLev;
                excerptMatchLev = snippet?.excerpt?.matchLevel || excerptMatchLev; 
                
                // Exception means Algolia did not produce highlight snippets for this hit.
                let exception = false;    
                if (titleMatchLev === 'none' && fulltextMatchLev === 'none' && excerptMatchLev === 'none') {
                    exception = true;
                }

                // Prefer highlighted title/excerpt when match metadata is available.
                if (snippet?.title?.value && titleMatchLev !== 'none') {
                    title = parse(`${snippet.title.value}`, parseOptions);
                }

                //excerpt & fulltext scan, display highlight
                //either fulltext or excerpt would become the "description"
                if (snippet?.fulltext?.value && fulltextMatchLev !== 'none') {
                    description = parse(`${snippet.fulltext.value}`, parseOptions);
                } else if (snippet?.excerpt?.value && excerptMatchLev !== 'none') {
                    description = parse(`${snippet.excerpt.value}`, parseOptions);
                }

                rows.push(
                    <Row
                        key={result.article_id || `result-${rs}`}
                        className={styles.resultHolder}
                        onClick={() => { viewArticle(result.article_id) }}
                    >
                        <Col lg={3} md={4} xs={12} className={styles.resultLeft}>
                            {result.thumbnail && (
                                <div className={styles.thumbnailWrapper}>
                                    <Image
                                        src={result.thumbnail}
                                        className={styles.thumbnail}
                                        alt="圖片"
                                        sizes="(max-width: 768px) 100vw, 25vw"
                                        style={{
                                            width: '100%',
                                            height: 'auto',
                                            objectFit: 'cover',
                                        }}
                                        height={220}
                                        width={400}
                                        placeholder="blur"
                                        blurDataURL={LIB.getBasepath('/images/loading.png')}
                                    />
                                </div>
                            )}
                        </Col>
                        <Col lg={9} md={8} xs={12} className={styles.resultRight}>
                            <div className={styles.title}>{title}</div>
                            <div className={styles.excerpt}>...{description}...</div>
                            <div className={styles.exceptionResultTxt}>{exception && exceptionResultTxt}</div>
                            <div className={styles.extraInfo}>
                                <div className={styles.date}>{result.post_date.substring(0, 10)}</div>
                                <div className={styles.cat}>分類：{topics[result.topic_id]}</div>
                            </div>
                        </Col>
                    </Row>
                );
            }
        } else {
            return (
                <div className={styles.empty}><span className={styles.slogan}>認 識 </span> 我 們  • 從 <span className={styles.slogan}> 這 裡 </span> 開 始</div>
            )
        }

        return (
            <Container>
                {rows}
            </Container>
        );
    };

    //to simplify result count bar function, avoid to many arguments
    const resultCountBarArgs = useMemo(() => ({
        didSearchTxt: didSearchTxt,
        resultCount: resultCount,
        openCategory: openCategory,
        setOpenCategory: setOpenCategory,
        searchMore: searchMore,
        preciseMode: preciseMode,
        category: category,
        setCategory: setCategory,
        onCategoryComplete: onCategoryComplete,
        openDateFilter: openDateFilter,
        setOpenDateFilter: setOpenDateFilter,
        dateFilter: dateFilter,
        setDateFilter: setDateFilter,
        onDateFilterComplete: onDateFilterComplete,
        filterData: filterData,
        sorting: sorting,
        setSorting: setSorting,
        openSorting: openSorting,
        setOpenSorting: setOpenSorting,
        onSortingComplete: onSortingComplete,
        setDateRangeUnix: setDateRangeUnix,
    }), [didSearchTxt, resultCount, openCategory, searchMore, preciseMode, category, onCategoryComplete, openDateFilter, dateFilter, onDateFilterComplete, filterData, sorting, openSorting, onSortingComplete]);

    //end point of the view
    return (
        <div className={styles.fullWidth}>
            <Header />

            <SearchBar
                searchTxt={searchTxt}
                onchangevalue={onchangevalue}
                doSearch={clickSearchBtn}
                setPreciseMode={setPreciseMode}
            />

            <Keyword
                keywords={keywords}
                pressKeyword={pressKeyword}
            />
            
            <ResultCountBar
                rcbArg={resultCountBarArgs}
                didSearchTxt={didSearchTxt}
            />

            {errorMsg && <Container><div className={styles.empty}>{errorMsg}</div></Container>}
            {/* Keep previous hits visible while refreshing to avoid thumbnail remount flash. */}
            {isLoading && searchResult.length === 0 && (
                <Container><div className={styles.empty}>搜尋中...</div></Container>
            )}
            {!errorMsg && (searchResult.length > 0 || !isLoading) && renderResult()}

            <Pagination
                totalPageNum={totalPageNum}
                curPageNum={curPageNum}
                jumpPage={jumpPage}
                navigation={navigation}
            />

            <Footer />
        </div>
    );

}

export default SearchView;